import React, { useState } from "react";
import {
  FaArrowRight,
  FaEnvelope,
  FaEnvelopeOpenText,
  FaExclamationTriangle,
  FaLifeRing,
  FaTrashAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  handleAccountDeletionError,
  requestAccountDeletion,
} from "../api/account-deletion";
import { Button, Field, Input, Spinner, Textarea } from "../ui";
import AuthLayout from "../components/site/AuthLayout";

const SUPPORT_EMAIL = "tarbaa.kw@gmail.com";

const SupportLink = ({ children, className = "" }) => (
  <a
    href={`mailto:${SUPPORT_EMAIL}`}
    className={`font-bold text-primary hover:text-mainText underline-offset-4 hover:underline ${className}`}
  >
    {children}
  </a>
);

const AccountDeletionRequest = () => {
  const [formData, setFormData] = useState({
    email: "",
    reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [deletionId, setDeletionId] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.email.trim()) {
      toast.error("الرجاء إدخال البريد الإلكتروني");
      return;
    }

    if (!validateEmail(formData.email.trim())) {
      toast.error("الرجاء إدخال بريد إلكتروني صحيح");
      return;
    }

    setLoading(true);

    try {
      const response = await requestAccountDeletion(
        formData.email,
        formData.reason
      );

      if (response.success) {
        setSubmitted(true);
        setDeletionId(response.deletionId);
        toast.success("تم إرسال طلب حذف الحساب إلى بريدك الإلكتروني");
      } else {
        toast.error(response.message || "حدث خطأ أثناء إرسال الطلب");
      }
    } catch (error) {
      console.error("Account deletion request error:", error);
      const errorMessage = handleAccountDeletionError(error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const supportFooter = (
    <>
      إذا واجهت أي مشاكل، تواصل مع <SupportLink>فريق الدعم</SupportLink>
    </>
  );

  if (submitted) {
    return (
      <AuthLayout title="تم إرسال طلب الحذف" footer={supportFooter}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-5">
            <FaEnvelopeOpenText className="text-2xl" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-bold">تحقق من بريدك الإلكتروني</h2>
          <p className="text-white/70 mt-2">
            أرسلنا رابط تأكيد حذف الحساب إلى:
          </p>
          <p dir="ltr" className="text-lg font-bold text-primary my-2 break-all">
            {formData.email}
          </p>
          <p className="text-sm text-white/70 leading-relaxed">
            تحقق من البريد الوارد وصندوق الرسائل المزعجة واتبع التعليمات.
          </p>
        </div>

        <div className="mt-6 rounded-xl bg-white/[0.05] border border-white/10 p-4 text-sm text-white/75">
          <p className="font-bold text-white mb-2 flex items-center gap-2">
            <FaExclamationTriangle className="text-primary shrink-0" aria-hidden="true" />
            تنبيهات مهمة
          </p>
          <ul className="list-disc pr-5 space-y-1">
            <li>سينتهي رابط التأكيد خلال 15 دقيقة</li>
            <li>تحقق من صندوق الرسائل المزعجة</li>
            <li>يمكنك إلغاء الطلب في أي وقت قبل التأكيد</li>
          </ul>
        </div>

        <div className="mt-6 space-y-3">
          <Button block to="/">
            العودة إلى الصفحة الرئيسية
          </Button>
          <Button
            block
            variant="secondary"
            onClick={() => {
              setSubmitted(false);
              setFormData({ email: "", reason: "" });
            }}
          >
            طلب جديد
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="طلب حذف الحساب"
      subtitle="نأسف لرؤيتك تغادر مجتمع تربع"
      footer={supportFooter}
    >
      <div className="rounded-xl bg-danger/10 border border-danger/40 p-4">
        <p className="font-bold text-danger mb-2 flex items-center gap-2">
          <FaExclamationTriangle className="shrink-0" aria-hidden="true" />
          تحذير مهم جداً
        </p>
        <ul className="list-disc pr-5 space-y-1 text-sm text-white/85">
          <li>
            سيتم حذف حسابك <strong className="text-white">نهائياً</strong> ولا
            يمكن استرداده
          </li>
          <li>ستفقد جميع بياناتك: النقاط، الإنجازات، الأصدقاء</li>
          <li>لن تتمكن من استخدام نفس البريد الإلكتروني لمدة 30 يوماً</li>
        </ul>
      </div>

      <div className="mt-4 rounded-xl bg-white/[0.05] border border-white/10 p-4 text-sm">
        <p className="font-bold text-white mb-2 flex items-center gap-2">
          <FaLifeRing className="text-primary shrink-0" aria-hidden="true" />
          هل تريد المساعدة بدلاً من الحذف؟
        </p>
        <p className="text-white/70 leading-relaxed mb-3">
          إذا كنت تواجه مشكلة أو لديك شكوى، نحن هنا لمساعدتك. ربما نستطيع حل
          المشكلة بدلاً من حذف حسابك.
        </p>
        <SupportLink className="inline-flex items-center gap-2">
          <FaEnvelope aria-hidden="true" />
          تواصل مع فريق الدعم
        </SupportLink>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        <Field label="البريد الإلكتروني" htmlFor="deletion-email">
          <Input
            ltr
            type="email"
            id="deletion-email"
            name="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            placeholder="example@domain.com"
            disabled={loading}
          />
        </Field>

        <Field label="سبب طلب الحذف (اختياري)" htmlFor="deletion-reason">
          <Textarea
            id="deletion-reason"
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            maxLength={500}
            rows={4}
            className="resize-none"
            placeholder="يساعدنا سبب الحذف في تحسين خدماتنا..."
            disabled={loading}
          />
          <p className="text-xs text-white/55 text-left">
            {formData.reason.length}/500 حرف
          </p>
        </Field>

        <div className="space-y-3">
          <Button
            type="submit"
            variant="danger"
            size="lg"
            block
            disabled={loading || !formData.email.trim()}
            icon={loading ? <Spinner /> : <FaTrashAlt aria-hidden="true" />}
          >
            {loading ? "جارٍ الإرسال..." : "طلب حذف الحساب نهائياً"}
          </Button>

          <Button
            block
            variant="secondary"
            to="/"
            icon={<FaArrowRight className="text-xs" aria-hidden="true" />}
          >
            إلغاء والعودة للرئيسية
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AccountDeletionRequest;
